import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  MixpanelEvent,
  Tab,
  tabLabelerEnumerated,
  TabList,
  TabPanels,
  Tabs,
  TabSize,
  useCallbackRef,
  useConstant,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type BlotterTableFilter,
  type TabProps,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { useBlotterTableTabCloning } from '../../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { DEFAULT_OTC_TABS, NEW_OTC_TAB_DEFAULTS } from './defaults';
import { FilteredOTCDerivatives } from './FilteredOTCDerivatives';
import { OTC_BLOTTER_PREFIX } from './tokens';

export interface OTCTabProps extends TabProps {
  defaultFilter: BlotterTableFilter;
}

const tabLabeler = tabLabelerEnumerated('OTC Derivatives');
function getBlotterID(tabID: string | undefined) {
  return `${OTC_BLOTTER_PREFIX}/${tabID}`;
}

export function OTCDerivatives() {
  const mixpanel = useMixpanel();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallbackRef(() => setInitialFiltersOpen(false));
  const handleAdd = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  });

  const defaultInitialItems = useConstant(DEFAULT_OTC_TABS);

  const persistedTabs = usePersistedTabs<OTCTabProps>(OTC_BLOTTER_PREFIX, {
    defaultInitialItems,
    defaultInitialSelectedIndex: 0,
    onSelect: handleSelect,
  });

  const tabs = useTabs<OTCTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallback(() => {
    tabs.clone(tabs.selectedIndex);
  }, [tabs]);

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Tabs {...tabs} h="100%" size={TabSize.Large}>
      <TabList isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} reorderable={true} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map((tab, idx) => {
          return (
            <AccordionGroup key={tab.id}>
              <FilteredOTCDerivatives
                key={idx}
                blotterPersistID={`${OTC_BLOTTER_PREFIX}/${tab.id}`}
                tabLabel={tab.label}
                defaultFilter={'defaultFilter' in tab ? tab.defaultFilter : NEW_OTC_TAB_DEFAULTS.defaultFilter}
                onCloneTab={handleCloneTab}
                initialIsOpen={initialFiltersOpen}
              />
            </AccordionGroup>
          );
        })}
      </TabPanels>
    </Tabs>
  );
}

export default OTCDerivatives;
